import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, CalendarClock, CheckCircle2, CreditCard, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { SelectField } from '@/components/ui/select';
import { DatePicker } from '@/components/ui/date-picker';
import { SavedAddressPicker } from '@/components/saved-address-picker';
import { form_error_message } from '@/lib/forms';
import { use_pets } from '@/features/pet/hooks';
import {
  use_preview_walk_plan,
  use_create_walk_plan,
  use_pay_walk_plan,
  use_verify_walk_plan_payment,
} from '@/features/walk-plan/hooks';
import type { WalkPlan, WalkPlanPreview } from '@/features/walk-plan/types';
import { fetch_my_walk_plans } from '@/features/walk-plan/api';
import type { DiscoveryService } from '@/features/discovery/types';
import type { ServicePackage } from '@/features/service-package/types';
import { use_public_service_packages } from '@/features/service-package/hooks';
import { open_razorpay_checkout } from '@/features/booking/razorpay-checkout';
import { use_session_store } from '@/lib/session-store';

const IST = 'Asia/Kolkata';
const today_ist = () => new Date().toLocaleDateString('en-CA', { timeZone: IST });
const DAYS = [
  { value: 0, label: 'Sun' },
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
];

function walk_datetime(iso: string): string {
  return new Date(iso).toLocaleString('en-IN', { timeZone: IST, weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

type NavState = { service?: DiscoveryService; offer?: ServicePackage; vendor_name?: string; vendor_id?: string };
type Step = 'build' | 'preview' | 'pay' | 'done';

export function PlanPage() {
  const navigate = useNavigate();
  const { packageId } = useParams<{ packageId: string }>();
  const state = (useLocation().state ?? {}) as NavState;
  const service = state.service;
  // Client change (9 Oct 2026): the customer buys the walker's package and only picks days, time and start.
  const offers_q = use_public_service_packages(state.vendor_id ?? '', 'WALKER', { enabled: !state.offer && !!state.vendor_id });
  const offer = state.offer ?? offers_q.data?.find((o) => o.id === packageId);
  const required_days = offer?.sessions_per_week ?? 0;
  const phone = use_session_store((s) => s.phone) ?? undefined;

  const pets_q = use_pets();
  const pets = pets_q.data ?? [];

  const preview_m = use_preview_walk_plan();
  const create_m = use_create_walk_plan();
  const pay_m = use_pay_walk_plan();
  const verify_m = use_verify_walk_plan_payment();

  const [step, set_step] = useState<Step>('build');
  const [days, set_days] = useState<number[]>([]);
  const [time, set_time] = useState('09:00');
  const [start_date, set_start_date] = useState(today_ist());
  const [pet_id, set_pet_id] = useState('');
  const [saved_address_id, set_saved_address_id] = useState<string | undefined>(undefined);
  const [pickup_address, set_pickup_address] = useState('');
  const [pickup_landmark, set_pickup_landmark] = useState('');
  const [pickup_pincode, set_pickup_pincode] = useState('');
  const [contact_phone, set_contact_phone] = useState('');
  const [access_notes, set_access_notes] = useState('');
  const [error, set_error] = useState<string | null>(null);

  const [preview, set_preview] = useState<WalkPlanPreview | null>(null);
  const [plan, set_plan] = useState<WalkPlan | null>(null);

  useEffect(() => {
    if (phone && !contact_phone) set_contact_phone(phone);
  }, [phone, contact_phone]);

  const schedule = useMemo(
    () => ({ package_id: packageId ?? '', days_of_week: [...days].sort((a, b) => a - b), time, start_date }),
    [packageId, days, time, start_date],
  );

  // The package promises a fixed count; fewer fitting dates means this schedule can't be bought.
  const short_by = preview ? Math.max(0, preview.required_walks - preview.total_walks) : 0;

  function toggle_day(d: number) {
    set_days((cur) => (cur.includes(d) ? cur.filter((x) => x !== d) : cur.length >= required_days ? cur : [...cur, d]));
  }

  async function run_preview() {
    set_error(null);
    if (!offer) return;
    if (days.length !== required_days) {
      return set_error(`Pick exactly ${required_days} day${required_days > 1 ? 's' : ''} of the week.`);
    }
    try {
      const p = await preview_m.mutateAsync(schedule);
      if (p.total_walks === 0) {
        set_error("No walks match that schedule. Bookings need at least a day's notice, so today never has slots — try starting tomorrow or later, or pick a different time the walker is open.");
        set_preview(null);
        return;
      }
      set_preview(p);
      set_step('preview');
    } catch (err) {
      set_error(form_error_message(err, 'Could not preview the plan.'));
    }
  }

  async function confirm_plan() {
    set_error(null);
    if (!pet_id) return set_error('Choose which dog the plan is for.');
    if (!pickup_address.trim()) return set_error('Enter a pickup address.');
    if (!contact_phone.trim()) return set_error('Enter a contact phone.');
    try {
      const created = await create_m.mutateAsync({
        ...schedule,
        pet_id,
        saved_address_id,
        pickup_address: pickup_address.trim(),
        pickup_landmark: pickup_landmark.trim() || undefined,
        pickup_pincode: pickup_pincode.trim() || undefined,
        contact_phone: contact_phone.trim(),
        access_notes: access_notes.trim() || undefined,
      });
      set_plan(created);
      set_step('pay');
    } catch (err) {
      set_error(form_error_message(err, 'Could not create the plan.'));
    }
  }

  async function pay() {
    if (!plan) return;
    set_error(null);
    try {
      // Re-check the plan's live status before opening a gateway sheet. `plan` (and its
      // `checkout` descriptor) is cached in state from creation time, so a plan cancelled
      // in another tab — or swept by the 15-minute unpaid-plan cron — still rendered a
      // working Pay button, and the server only objected *after* the customer had paid
      // (BUG-043). Better to refuse before taking their money than to refund it after.
      const live = (await fetch_my_walk_plans()).find((p) => p.id === plan.id);
      if (live && live.status !== 'PENDING_PAYMENT') {
        set_plan(live);
        set_error(
          live.status === 'CANCELLED'
            ? 'This plan was cancelled, so it can no longer be paid for. Please set up a new plan.'
            : `This plan is ${live.status.toLowerCase().replace(/_/g, ' ')} and is not awaiting payment.`,
        );
        return;
      }

      if (plan.checkout) {
        await open_razorpay_checkout({
          checkout: plan.checkout,
          service_name: `${service?.name ?? 'Walk'} plan`,
          contact: phone,
          on_success: async (resp) => {
            try {
              const verified = await verify_m.mutateAsync({
                id: plan.id,
                input: {
                  razorpay_order_id: resp.razorpay_order_id,
                  razorpay_payment_id: resp.razorpay_payment_id,
                  razorpay_signature: resp.razorpay_signature,
                },
              });
              set_plan(verified);
              set_step('done');
            } catch (err) {
              set_error(form_error_message(err, 'Payment verification failed. If you were charged, contact support.'));
            }
          },
          on_dismiss: () => set_error('Payment cancelled. You can try again.'),
          on_failure: (message) => set_error(message),
        });
      } else {
        const paid = await pay_m.mutateAsync(plan.id);
        set_plan(paid);
        set_step('done');
      }
    } catch (err) {
      set_error(form_error_message(err, 'Payment failed. Please try again.'));
    }
  }

  const paying = pay_m.isPending || verify_m.isPending;

  return (
    <div className="mx-auto flex w-full max-w-xl flex-col gap-5">
      <Button
        variant="ghost"
        size="sm"
        className="self-start"
        onClick={() => navigate(state.vendor_id ? `/app/discover/${state.vendor_id}` : '/app/discover')}
      >
        <ArrowLeft className="h-4 w-4" /> Back
      </Button>

      <div>
        <h1 className="font-heading text-2xl font-bold">Set up a walk plan</h1>
        <p className="text-sm text-muted-foreground">
          {[offer?.name, state.vendor_name, offer ? `${offer.duration_days} days · ${offer.sessions_count} walk${offer.sessions_count > 1 ? 's' : ''} · ₹${offer.price_inr}` : null].filter(Boolean).join(' · ')}
        </p>
      </div>

      {!offer && (
        <Card data-testid="plan-no-offer">
          <CardContent className="p-5 text-sm text-muted-foreground">
            {offers_q.isLoading ? 'Loading package…' : 'Open this package from the walker’s page to book it.'}
          </CardContent>
        </Card>
      )}

      {/* Step 1 — schedule builder */}
      {step === 'build' && offer && (
        <Card>
          <CardContent className="flex flex-col gap-4 p-5">
            <div className="flex flex-col gap-1.5">
              <Label>
                Pick {required_days} day{required_days > 1 ? 's' : ''} of the week ({days.length}/{required_days})
              </Label>
              <div className="flex flex-wrap gap-1.5" data-testid="plan-days">
                {DAYS.map((d) => {
                  const active = days.includes(d.value);
                  return (
                    <button
                      key={d.value}
                      type="button"
                      onClick={() => toggle_day(d.value)}
                      data-testid={`plan-day-${d.value}`}
                      data-active={active}
                      className={
                        'h-10 w-12 rounded-button border-2 text-sm font-semibold transition-all duration-fast ' +
                        (active
                          ? 'border-primary bg-primary/10 text-primary shadow-clay-sm'
                          : 'border-input bg-card text-muted-foreground hover:border-primary/40')
                      }
                    >
                      {d.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="plan-time">Time</Label>
                <Input id="plan-time" type="time" value={time} onChange={(e) => set_time(e.target.value)} data-testid="plan-time" />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="plan-start">Start date</Label>
                <DatePicker id="plan-start" min={today_ist()} value={start_date} onChange={set_start_date} data-testid="plan-start" />
              </div>
            </div>

            <p className="rounded-input border-2 border-input p-3 text-sm text-muted-foreground" data-testid="plan-offer-summary">
              {offer.sessions_count} walk{offer.sessions_count > 1 ? 's' : ''} within {offer.duration_days} days from your start date ·{' '}
              <span className="font-semibold text-foreground">₹{offer.price_inr}</span> total
            </p>

            {error && <p className="text-sm text-destructive" data-testid="plan-error">{error}</p>}

            <Button onClick={run_preview} disabled={preview_m.isPending} variant="gradient" data-testid="plan-preview">
              {preview_m.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Preview walks
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Step 2 — preview + dog + pickup → confirm */}
      {step === 'preview' && preview && (
        <Card data-testid="plan-preview-screen">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CalendarClock className="h-5 w-5 text-primary" /> {preview.total_walks} walks · ₹{preview.total_inr}
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4 p-5 pt-0">
            {short_by > 0 && (
              <p className="rounded-input border-2 border-destructive p-2 text-xs text-destructive" data-testid="plan-short">
                Only {preview.total_walks} of {preview.required_walks} fit — the walker isn't available on some of those dates. Go back and try another time or start date.
              </p>
            )}
            <ul className="max-h-48 divide-y divide-border overflow-y-auto rounded-md border border-border" data-testid="plan-walk-list">
              {preview.walks.map((w, i) => (
                <li key={i} className="flex items-center justify-between px-3 py-2 text-sm">
                  <span>{walk_datetime(w.slot_start)}</span>
                  <span className="tabular-nums text-muted-foreground">₹{w.price_inr}</span>
                </li>
              ))}
            </ul>

            {pets.length === 0 ? (
              <div className="flex items-center justify-between gap-3 rounded-input border-2 border-warning/30 bg-warning/5 p-3 text-sm" data-testid="plan-needs-pet">
                <span>Add your dog before setting up a plan.</span>
                <Button size="sm" variant="outline" onClick={() => navigate('/app/pets/new')}>Add a dog</Button>
              </div>
            ) : (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="plan-pet">Dog</Label>
                <SelectField
                  id="plan-pet"
                  value={pet_id}
                  onValueChange={set_pet_id}
                  data-testid="plan-pet"
                  options={[{ value: '', label: 'Select your dog' }, ...pets.map((p) => ({ value: p.id, label: p.name }))]}
                />
              </div>
            )}

            <div className="flex flex-col gap-3 rounded-input border-2 border-brand-outline-variant/15 bg-secondary/30 p-3" data-testid="plan-pickup-fields">
              <p className="text-sm font-semibold">Pickup details</p>
              <SavedAddressPicker
                on_select={(a) => {
                  set_saved_address_id(a.id);
                  set_pickup_address([a.address, a.city].filter(Boolean).join(', '));
                  set_pickup_landmark(a.landmark ?? '');
                  set_pickup_pincode(a.pincode ?? '');
                  if (a.phone) set_contact_phone(a.phone);
                  if (a.access_notes) set_access_notes(a.access_notes);
                }}
                on_clear={() => {
                  set_saved_address_id(undefined);
                  set_pickup_address('');
                  set_pickup_landmark('');
                  set_pickup_pincode('');
                }}
              />
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="plan-address">Address</Label>
                <Input
                  id="plan-address"
                  value={pickup_address}
                  onChange={(e) => {
                    set_saved_address_id(undefined);
                    set_pickup_address(e.target.value);
                  }}
                  placeholder="House / building, street, area"
                  data-testid="plan-address"
                />
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="plan-landmark">Landmark (optional)</Label>
                  <Input id="plan-landmark" value={pickup_landmark} onChange={(e) => set_pickup_landmark(e.target.value)} placeholder="Near…" data-testid="plan-landmark" />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="plan-pincode">Pincode (optional)</Label>
                  <Input id="plan-pincode" value={pickup_pincode} onChange={(e) => set_pickup_pincode(e.target.value)} placeholder="560001" data-testid="plan-pincode" />
                </div>
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="plan-contact">Contact phone</Label>
                <Input id="plan-contact" value={contact_phone} onChange={(e) => set_contact_phone(e.target.value)} placeholder="+91…" data-testid="plan-contact" />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="plan-access">Access notes (optional)</Label>
                <Input id="plan-access" value={access_notes} onChange={(e) => set_access_notes(e.target.value)} placeholder="Gate code, floor, where to meet…" data-testid="plan-access" />
              </div>
            </div>

            {error && <p className="text-sm text-destructive" data-testid="plan-error">{error}</p>}

            <div className="flex items-center justify-between gap-2">
              <Button variant="ghost" onClick={() => set_step('build')} data-testid="plan-back">Edit schedule</Button>
              <Button variant="gradient" onClick={confirm_plan} disabled={create_m.isPending || short_by > 0} data-testid="plan-confirm">
                {create_m.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Continue to payment
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3 — one payment for the whole plan */}
      {step === 'pay' && plan && (
        <Card data-testid="plan-payment-screen">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-primary" /> Payment
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4 p-5 pt-0">
            <div className="rounded-input border-2 border-brand-outline-variant/15 bg-secondary/40 p-3 text-sm">
              <div className="flex justify-between py-1">
                <span className="text-muted-foreground">{plan.total_walks}-walk plan</span>
                <span className="font-semibold tabular-nums">₹{plan.total_inr}</span>
              </div>
              <div className="flex justify-between py-1">
                <span className="text-muted-foreground">From</span>
                <span>{plan.start_date} · {plan.duration_days ?? plan.weeks * 7} days</span>
              </div>
            </div>
            <p className="rounded-input border-2 border-dashed border-brand-outline-variant/30 p-2 text-center text-xs text-muted-foreground">
              {plan.checkout ? 'Secure payment via Razorpay — one charge for the whole plan.' : 'Demo gateway — no real charge. This always succeeds.'}
            </p>

            {error && <p className="text-sm text-destructive" data-testid="plan-pay-error">{error}</p>}

            <Button onClick={pay} disabled={paying} variant="gradient" data-testid="plan-pay-now">
              {paying ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Pay ₹{plan.total_inr}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Step 4 — confirmation */}
      {step === 'done' && plan && (
        <Card data-testid="plan-confirmation">
          <CardContent className="flex flex-col items-center gap-3 p-8 text-center">
            <CheckCircle2 className="h-12 w-12 text-success" />
            <h2 className="font-heading text-xl font-bold">Plan booked</h2>
            <p className="text-sm text-muted-foreground">
              Payment captured for all {plan.total_walks} walks. Each walk was sent to the walker — you'll be notified as they confirm.
            </p>
            <div className="flex gap-2">
              <Button onClick={() => navigate('/app/bookings')} variant="gradient" data-testid="plan-go-to-bookings">
                View my walks
              </Button>
              <Button onClick={() => navigate('/app/discover')} variant="ghost">Keep browsing</Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
